import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import styles from "./Dropzone.module.scss";
import { UploadIcon } from "../Icons/Upload";
import DescriptionIcon from "@mui/icons-material/Description";
import classNames from "classnames";
import { FileWithPreview } from "../../types/common";

interface DropzoneProps {
  fullWidth?: boolean;
  disabled?: boolean;
  handleAbort?: boolean;
  fileType?: "image" | "doc";
  files: FileWithPreview[];
  setFiles: (files: FileWithPreview[]) => void;
  multiple?: boolean;
  maxUploadingFiles?: number;
}

export const Dropzone = ({
  fullWidth = false,
  disabled = false,
  handleAbort,
  fileType = "image",
  files,
  setFiles,
  multiple = false,
  maxUploadingFiles = 30,
}: DropzoneProps) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (handleAbort) {
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleAbort]);

  useEffect(() => {
    setError(null);
  }, [files]);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: disabled || maxUploadingFiles === 0,
    multiple: multiple,
    maxFiles: maxUploadingFiles,
    accept:
      fileType === "image"
        ? {
            "image/*": [],
          }
        : {
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
              [".docx"],
            "application/pdf": [".pdf"],
          },
    // accept: '.doc, .docx, .pdf'
    maxSize: fileType === "image" ? 5 * 1024 * 1024 : undefined, // 5MB for images
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections?.length > 0) {
        const errorMessages = fileRejections.map(
          (rej) => rej.errors?.[0]?.message || "Unknown error",
        );
        const uniqueErrorMessages = [...new Set(errorMessages)];
        const errorMessageString = uniqueErrorMessages.join(", ");
        setError(errorMessageString);
      } else {
        setFiles(
          acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            }) as FileWithPreview,
          ),
        );
      }
    },
  });

  const handleRemove = (preview: string) => {
    const updatedFiles = files.filter((item) => item.preview !== preview);
    setFiles(updatedFiles);
  };


  const thumbs = files.map((file) => (
    <div className={styles.thumb} key={file.name}>
      <div className={styles.thumbInner}>
        {fileType === "image" ? (
          <img
            src={file.preview}
            alt={`preview-${file.name}`}
            onLoad={() => URL.revokeObjectURL(file.preview)}
          />
        ) : (
          <div className={styles.filePreview}>
            <DescriptionIcon fontSize="small" />
            <p>{file.name}</p>
          </div>
        )}
        <button
          className={styles.remove}
          onClick={() => handleRemove(file.preview)}
        >
          X
        </button>
      </div>
    </div>
  ));

  useEffect(() => {
    // Revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  return (
    <section
      className={classNames(styles.dropzoneContainer, {
        [styles.fullWidth]: fullWidth,
        [styles.disabled]: disabled,
      })}
    >
      <div className={styles.dropzone} {...getRootProps()}>
        <input {...getInputProps()} />
        <UploadIcon />
        <p className={styles.text}>
          <span>Vyberte</span> nebo přetáhněte sem <br />
          {fileType === "image" ? "SVG, PNG, JPG or GIF" : "DOC, DOCX or PDF"}
          {fileType === "image"
            ? " (max. 30 souborů a 5MB na soubor)"
            : "(max. 30 souborů)"}
        </p>
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
      {thumbs?.length > 0 ? (
        <aside className={styles.preview}>{thumbs}</aside>
      ) : null}
    </section>
  );
};

